import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Card, CardMedia, Chip, CircularProgress, Container, MenuItem, Paper, Stack, TextField } from '@mui/material';
import PageHeader from 'components/common/PageHeader/PageHeader';
import CustomText from 'components/common/CustomText/CustomText';
import TitleText from 'components/common/CustomText/TitleText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import { currencies } from 'common/constants';
import { useGetStores } from 'hooks/useStore';
import { SmartSearchStep, useSmartSearchStream } from 'hooks/useSearch';
import { SmartSearchDebug } from 'services/searchService';

// The app's search, tried here as a shopper would: the products come back as
// the app shows them, and below them, in plain words, how the search read the
// words and why these products - so a bad answer can be traced to its step.
export default function SearchTestPage() {
  const globalStyles = useGlobalStyles();
  const [input, setInput] = useState('');
  const [storeId, setStoreId] = useState('');

  // Every store fits one page of this size today.
  const { data: stores = [] } = useGetStores(
    { page: 1, size: 100 },
    { select: (res: any) => res.data, refetchOnWindowFocus: false },
  );

  const { search: run, steps, data, isLoading, isError } = useSmartSearchStream();
  const search = () => {
    if (input.trim()) run({ input: input.trim(), ...(storeId ? { storeId } : {}) });
  };

  return (
    <Box className={globalStyles.container}>
      <Container maxWidth={'xl'} className={globalStyles.container}>
        <Box pt={2}>
          <PageHeader title={'Search test'} buttons={[]} />
        </Box>

        <Stack direction='row' spacing={2} mt={3} alignItems='center'>
          <TextField
            fullWidth
            size='small'
            placeholder='Type a search the way a shopper would, in any language'
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && search()}
          />
          <TextField select size='small' label='Store' value={storeId} onChange={(event) => setStoreId(event.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value=''>All stores (market)</MenuItem>
            {stores.map((store: any) => (
              <MenuItem key={store.id} value={store.id}>
                {store.info?.name ?? store.id}
              </MenuItem>
            ))}
          </TextField>
          <CustomButton text='Search' disabled={isLoading || !input.trim()} onClick={search} />
        </Stack>

        {/* Each step as it finishes, as the app shows it, and the one it is on. */}
        {isLoading && (
          <Stack spacing={1} mt={4}>
            {steps.map((step, index) => (
              <Stack key={index} direction='row' spacing={1} alignItems='flex-start'>
                <CustomText text='✓' color={colors.primary} fontWeight={600} />
                <CustomText text={stepText(step)} />
              </Stack>
            ))}
            <Stack direction='row' spacing={1} alignItems='center'>
              <CircularProgress size={16} />
              <CustomText text={steps.length ? 'Searching…' : 'Reading your search…'} color={colors.gray} />
            </Stack>
          </Stack>
        )}

        {isError && !isLoading && (
          <Box mt={4}>
            <CustomText text='The search failed.' color={colors.error} />
          </Box>
        )}

        {data && !isLoading && (
          <>
            <Box mt={4}>
              <CustomText
                text={
                  data.products.length === 0
                    ? 'No products found.'
                    : `${data.products.length} product${data.products.length === 1 ? '' : 's'}${data.suggested ? ' suggested for this situation' : ''}`
                }
                fontWeight={600}
                fontSize={18}
              />
              <CustomText text={`Took ${(data.tookMs / 1000).toFixed(1)}s · searched ${data.scope} products on sale`} fontSize={13} color={colors.gray} />
            </Box>

            <Box mt={2} display='flex' flexWrap='wrap' gap={2}>
              {data.products.map((storeProduct: any) => (
                <ProductCard key={storeProduct.id} storeProduct={storeProduct} />
              ))}
            </Box>

            <Explanation data={data} />
          </>
        )}
      </Container>
    </Box>
  );
}

// A finished step, with what it found.
function stepText(step: SmartSearchStep): string {
  switch (step.step) {
    case 'understood':
      return `Understood: ${(step.pieces ?? []).join(', ') || 'nothing specific'}`;
    case 'aisles':
      return `Looking in ${step.categories?.length ?? 0} aisles: ${(step.categories ?? []).join(', ') || 'every aisle'}`;
    case 'suggesting':
      return `Suggesting from: ${(step.categories ?? []).join(', ')}`;
    case 'matching':
      return `Found ${step.found ?? 0} matching products`;
    case 'checking':
      return 'Doing a final check';
    default:
      return step.step;
  }
}

// The product as the app shows it: its picture, name, brand and price - opening
// the store's own listing, the one whose price is shown.
export function ProductCard({ storeProduct }: { storeProduct: any }) {
  const product = storeProduct.product;
  const name = product?.info?.name;
  const image = product?.avatar?.medium;

  return (
    <Card
      component={Link}
      to={`/stores/all-stores/${storeProduct.store?.id}/store-products/${storeProduct.id}`}
      target='_blank'
      rel='noopener noreferrer'
      sx={{ display: 'block', width: 180, borderRadius: 3, boxShadow: 3, textDecoration: 'none', color: 'inherit' }}
    >
      {image ? (
        <CardMedia component='img' image={image} alt={name} sx={{ width: 180, height: 180, objectFit: 'cover' }} />
      ) : (
        <Box sx={{ width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.topBackground }}>
          <CustomText text='No image' fontSize={13} color={colors.gray} />
        </Box>
      )}
      <Stack p={1} gap={0.5}>
        <TitleText text={name ?? '—'} title={name ?? ''} fontWeight={600} textAlign='left' fontSize={14} color={colors.black} lineHeight={1.2} noWrap />
        <CustomText text={product?.tag?.info?.name ?? ''} fontSize={12} color={colors.gray} noWrap />
        <CustomText text={`${currencies.shekel}${Number(storeProduct.price).toFixed(2)}`} fontSize={13} fontWeight={600} color={colors.primary} />
      </Stack>
    </Card>
  );
}

// What each kind of piece means, in words.
const PIECE_LABELS: Record<string, string> = {
  product: 'Product',
  brand: 'Brand',
  person: 'For',
  problem: 'Problem',
  goal: 'Goal',
  'skin type': 'Skin',
  'hair type': 'Hair',
  'scalp type': 'Scalp',
  contains: 'Must have',
  'free of': 'Must not have',
  'body part': 'On',
  time: 'When',
  situation: 'Situation',
};

// How the search read the words and why these products, step by step.
function Explanation({ data }: { data: SmartSearchDebug }) {
  const removed = data.parts.find((part) => part.startsWith('review removed:'))?.replace('review removed:', '').trim();
  const suggestedAisles = data.parts.find((part) => part.startsWith('suggested:'));
  const matched = data.parts
    .filter((part) => part.includes(' -> '))
    .map((part) => {
      const [piece, tags] = part.split(' -> ');
      return { piece, tags };
    });
  const forWhom = [
    data.gender === 'men' ? "men's products" : data.gender === 'women' ? "women's products" : null,
    data.age === 'child' ? 'products for babies and kids' : null,
  ].filter(Boolean);

  return (
    <Paper variant='outlined' sx={{ p: 3, mt: 4, mb: 4 }}>
      <CustomText text='Why these results' fontWeight={600} fontSize={16} />

      <Step title='1. How it read your search'>
        <Stack direction='row' flexWrap='wrap' gap={1}>
          {data.pieces.map((piece, index) => (
            <Chip key={index} size='small' label={`${PIECE_LABELS[piece.kind] ?? piece.kind}: ${piece.phrase}`} />
          ))}
        </Stack>
      </Step>

      <Step title={suggestedAisles ? '2. Aisles it suggested for this situation' : '2. Aisles it looked in'}>
        <CustomText text={data.categories.join(' · ') || 'Every aisle (no kind of product was named)'} />
      </Step>

      <Step title='3. Who the products are for'>
        <CustomText text={forWhom.length ? `Only ${forWhom.join(', ')}` : 'Anyone'} />
        {data.brandProducts !== null && <CustomText text={`Only this brand's products (${data.brandProducts})`} />}
      </Step>

      <Step title='4. What each product had to have'>
        {matched.length === 0 && <CustomText text='Nothing more - every product in those aisles fits.' />}
        {matched.map(({ piece, tags }, index) => (
          <CustomText key={index} text={`${piece} → any of: ${tags}`} />
        ))}
      </Step>

      {data.unmatched.length > 0 && (
        <Step title='Not in the store'>
          <CustomText text={`Nothing in the store matches: ${data.unmatched.join(', ')}`} color={colors.error} />
        </Step>
      )}

      <Step title='5. Taken out in a last check'>
        <CustomText text={removed && removed !== '-' ? removed : 'Nothing'} />
      </Step>
    </Paper>
  );
}

function Step({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box mt={2}>
      <CustomText text={title} fontWeight={600} fontSize={13} color={colors.gray} />
      <Box mt={0.5}>{children}</Box>
    </Box>
  );
}
